import { useCallback, useContext, useEffect, useRef, useState } from "react"
import _ from "lodash"

// Context
import { UserContext } from "../../../context/userContext"

//Kiosk components
import KioskAdminOrganization from "./Organization/KioskAdminOrganization"
import KioskAdminFormList from "./Forms/KioskAdminFormList"
import {
  kioskApiGetOrganizationsByEnterprise,
  kioskApiPostCloneOrganization,
} from "../kioskApi"

//GraphQL
import { useLazyQuery } from "@apollo/client"
import {
  GET_ORGANIZATIONS_BY_ENTERPRISE,
  GET_KIOSK_TEMPLATES_ORGANIZATION,
} from "../queries"

//PrimeReact
import { TabView, TabPanel } from "primereact/tabview"
import { Dropdown } from "primereact/dropdown"
import { Tooltip } from "primereact/tooltip"

//SOAR components
import EnterpriseLibrarySidebar from "../../../components/sidebars/EnterpriseLibrarySidebar"
import PageSidebarContent from "../../../components/sidebars/PageSidebarContent"
import { LogError } from "../../../helpers/logger"

//Styles
import myStyles from "../../../styles/kiosk/KioskAdmin.module.css"
import FirstTimeVisitorOptions from "./FirstTimeVisitorOptions/FirstTimeVisitorOptions"

//Additional Assessments Configs
import KioskAdditionalAssessmentsList from "./AdditionalAssessments/KioskAdditionalAssessmentsList"

//Activity Check In Options
import ActivityCheckInOptions from "./ActivityCheckInOptions/ActivityCheckInOptions"
import ParticipantManagement from "./ParticipantManagement/ParticipantManagement"

import Clone from "./Clone/Clone"
import CloneButton from "./CloneButton"

export default function KioskAdmin() {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingKioskTemplates, setIsLoadingKioskTemplates] = useState(true)
  const [allOrganizations, setAllOrganizations] = useState([])
  const [activeOrganizations, setActiveOrganizations] = useState([])
  const [selectedOrganization, setSelectedOrganization] = useState(undefined)
  const [targetOrganizations, setTargetOrganizations] = useState(undefined)
  const [selectedOptions, setSelectedOptions] = useState(undefined)
  const [showCloneDialog, setShowCloneDialog] = useState(false)
  const [kioskTemplates, setKioskTemplates] = useState([])

  const disableTabView =
    isLoading ||
    !activeOrganizations ||
    activeOrganizations?.length === 0 ||
    !selectedOrganization

  const topScroll = useRef()

  const [loadTemplates] = useLazyQuery(GET_KIOSK_TEMPLATES_ORGANIZATION, {
    fetchPolicy: "cache-and-network",
    variables: {
      organization_id: selectedOrganization?.organizationId,
    },
  })

  const loadTemplate = useCallback(async () => {
    try {
      if (selectedOrganization?.organizationId) {
        setIsLoadingKioskTemplates(true)
        const templateList = await loadTemplates({
          variables: {
            organization_id: selectedOrganization?.organizationId,
          },
        })

        if (templateList?.data?.getKioskTemplates) {
          setKioskTemplates(templateList?.data?.getKioskTemplates || [])
        } else {
          LogError("No templates found", templateList)
          setKioskTemplates([])
        }

        setIsLoadingKioskTemplates(false)
      } else {
        setKioskTemplates([])
        setIsLoadingKioskTemplates(false)
      }
    } catch (error) {
      LogError("Unable to load KIOSK templates", error)
      userCtx.setNotificationError("Unable to load Kiosk templates")
      setKioskTemplates([])
      setIsLoadingKioskTemplates(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization])

  useEffect(() => {
    loadTemplate()
  }, [loadTemplate])

  const [loadEnterpriseOrgs] = useLazyQuery(GET_ORGANIZATIONS_BY_ENTERPRISE, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      enterprise_id: userCtx.session.user.enterprise_id,
    },
  })

  const loadOrganizations = useCallback(async () => {
    try {
      //I've noticed when the page is reloaded the token is loading with null value at the very first
      if (userCtx.tokens.getKioskToken()) {
        setIsLoading(true)

        //Retrieve all organizations for the enterprise
        const allEnterpriseOrgs = await loadEnterpriseOrgs({
          variables: {
            staff_id: userCtx.session.user.staff_id,
            enterprise_id: userCtx.session.user.enterprise_id,
          },
        })

        if (allEnterpriseOrgs.error) {
          throw Error(allEnterpriseOrgs.error)
        }

        const useAllEnterpriseOrgs =
          allEnterpriseOrgs.data?.organizationsByEnterpriseUid || []

        setAllOrganizations(useAllEnterpriseOrgs)

        //Retrieve all active Kiosk organizations from the API
        const organizationList = await kioskApiGetOrganizationsByEnterprise(
          userCtx.tokens.getKioskToken(),
          userCtx.session.user.enterprise_id,
        )
        if (organizationList?.organizations) {
          const newOrganizationList = organizationList.organizations.map(
            (organization) => {
              let organizationProfile = useAllEnterpriseOrgs.find(
                (userOrganization) =>
                  userOrganization.organization_id ===
                  organization.organizationId,
              )
              if (organizationProfile) {
                organization.name = organizationProfile.organization_name
                organization.idStamp = organizationProfile.organization_id_stamp
              } else {
                organization.name = "Unknown organization"
                organization.idStamp = undefined
              }

              return organization
            },
          )
          setActiveOrganizations(
            newOrganizationList.filter(
              (f) => f.name !== "Unknown organization",
            ),
          )
        } else {
          setActiveOrganizations([])
        }

        const kioskOrg = organizationList?.organizations?.find(
          (org) => org.organizationId === userCtx.session.user.organization_id,
        )

        const contextOrg = useAllEnterpriseOrgs?.find(
          (org) => org.organization_id === userCtx.session.user.organization_id,
        )

        setSelectedOrganization({
          ..._.cloneDeep(contextOrg),
          organizationId: contextOrg.organization_id,
          name: contextOrg.organization_name,
          id: kioskOrg?.id,
        })

        setIsLoading(false)
      }
    } catch (error) {
      LogError("Unable to load organizations", error)
      userCtx.setNotificationError("Unable to load organizations")
      setActiveOrganizations([])
      setSelectedOrganization(undefined)
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session.user.enterprise_id])

  useEffect(() => {
    loadOrganizations()
  }, [loadOrganizations])

  useEffect(() => {
    if (topScroll.current) {
      topScroll.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [topScroll])

  const getOptions = () => {
    return [
      { label: "Form Customization", value: "forms" },
      { label: "First Time Visitor", value: "ftv" },
      { label: "Activity Check-in", value: "checkIn" },
      { label: "Additional Assessments", value: "assessments" },
    ]
  }
  const buildPayload = () => {
    return {
      sourceOrganizationId: selectedOrganization?.id,
      targetOrganizationIds: targetOrganizations,
      cloneForms: !!selectedOptions.find((o) => o === "forms"),
      cloneFirstTimeVisitors: !!selectedOptions.find((o) => o === "ftv"),
      cloneCheckInOptions: !!selectedOptions.find((o) => o === "checkIn"),
      cloneAdditionalAssessments: !!selectedOptions.find(
        (o) => o === "assessments",
      ),
      userId: userCtx.session.user.staff_id,
    }
  }
  const validate = () => {
    let valid = true
    if (!targetOrganizations || targetOrganizations.length === 0) {
      valid = false
      userCtx.setNotificationError(
        "Must select at least one target organization",
      )
    } else {
      if (!selectedOptions || selectedOptions.length === 0) {
        valid = false
        userCtx.setNotificationError("Must select at least one option to clone")
      }
    }
    return valid
  }
  const cloneOrganization = async () => {
    try {
      if (validate()) {
        setIsLoading(true)
        const payload = buildPayload()
        await kioskApiPostCloneOrganization(
          userCtx.tokens.getKioskToken(),
          payload,
        )
        setTargetOrganizations(undefined)
        setSelectedOptions(undefined)
        userCtx.setNotificationInformation("Clone process completed")
        setIsLoading(false)
        setShowCloneDialog(false)
      }
    } catch (error) {
      if (error?.response?.status === 500) {
        userCtx.setNotificationError(error?.response?.data)
      } else {
        userCtx.setNotificationError("Unable to clone organizations")
      }
      LogError("Unable to clone organizations", error)
      setTargetOrganizations(undefined)
      setSelectedOptions(undefined)
      setIsLoading(false)
    }
  }

  const buildSelectedOrganization = (organizationId) => {
    const kioskOrg = activeOrganizations.find(
      (org) => org.organizationId === organizationId,
    )
    let newSelectedOrg = allOrganizations.find(
      (x) => x.organization_id === organizationId,
    )

    if (newSelectedOrg) {
      newSelectedOrg = {
        ..._.cloneDeep(newSelectedOrg),
        organizationId: newSelectedOrg.organization_id,
        name: newSelectedOrg.organization_name,
        id: kioskOrg?.id,
      }

      setSelectedOrganization(newSelectedOrg)
    }
  }

  const OrganizationSelectorTemplate = (option) => {
    if (option) {
      return (
        <OrganizationTemplate
          organizationName={option.organization_name}
          organizationId={option.organization_id}
        />
      )
    } else if (selectedOrganization) {
      return (
        <OrganizationTemplate
          organizationId={selectedOrganization.organization_id}
          organizationName={selectedOrganization.organization_name}
        />
      )
    } else {
      return null
    }
  }

  const OrganizationTemplate = ({ organizationId, organizationName }) => {
    const isActiveOrg = activeOrganizations?.find(
      (org) => org.organizationId === organizationId,
    )
    const statusClass = isActiveOrg
      ? myStyles.organizationSelectorItemActive
      : myStyles.organizationSelectorItemDisabled

    const tooltip = isActiveOrg ? "Kiosk active" : "Kiosk disabled"

    return (
      <div className={myStyles.organizationSelectorItem}>
        <Tooltip target=".tt-organizationItem" />
        <div
          className={`${statusClass} tt-organizationItem`}
          data-pr-tooltip={tooltip}
          data-pr-position="bottom"
        >
          <span className="material-icons">check</span>
        </div>
        {organizationName}
      </div>
    )
  }

  const GetTabHeader = ({ header, tooltip }) => {
    return (
      <div>
        {tooltip && (
          <Tooltip target={".tt-kioskTabHeader"} className="custom-tooltip" />
        )}
        {header}{" "}
        {tooltip && (
          <i
            className={`pi pi-info-circle ${myStyles.info} tt-kioskTabHeader`}
            data-pr-tooltip={tooltip}
            data-pr-position="bottom"
          ></i>
        )}
      </div>
    )
  }

  return (
    <>
      <Clone
        cloneOrganization={cloneOrganization}
        visible={showCloneDialog}
        setVisible={setShowCloneDialog}
        getOptions={getOptions}
        isLoading={isLoading}
        organizations={activeOrganizations}
        selectedOptions={selectedOptions}
        selectedOrganization={selectedOrganization}
        setSelectedOptions={setSelectedOptions}
        setSelectedOrganization={buildSelectedOrganization}
        setTargetOrganizations={setTargetOrganizations}
        targetOrganizations={targetOrganizations}
      />
      <div ref={topScroll}>
        <PageSidebarContent>
          <EnterpriseLibrarySidebar />
        </PageSidebarContent>
        <div className="main-content">
          <div className={myStyles.header}>
            Kiosk management for{" "}
            {selectedOrganization && (
              <Dropdown
                value={selectedOrganization?.organizationId}
                options={allOrganizations}
                optionLabel="organization_name"
                optionValue="organization_id"
                onChange={(e) => {
                  buildSelectedOrganization(e.value)
                }}
                className={myStyles.headerOrganizations}
                valueTemplate={OrganizationSelectorTemplate}
                itemTemplate={OrganizationSelectorTemplate}
              />
            )}
            <CloneButton
              disabled={
                isLoading ||
                !selectedOrganization ||
                activeOrganizations?.length === 0
              }
              onClick={() => {
                setShowCloneDialog(true)
              }}
              isTabLevel={false}
            />
          </div>
          <div className={myStyles.tabView}>
            <CloneButton
              disabled={isLoading || !selectedOrganization}
              onClick={() => {
                setShowCloneDialog(true)
              }}
              isTabLevel={true}
            />
            <TabView>
              <TabPanel
                header={
                  <GetTabHeader
                    header={"Organization"}
                    tooltip={"Manage organization kiosk profile"}
                  />
                }
                disabled={disableTabView}
              >
                <KioskAdminOrganization
                  kiosk_organization_id={selectedOrganization?.id}
                  organization_id={selectedOrganization?.organizationId}
                  organization_id_stamp={
                    selectedOrganization?.organization_id_stamp
                  }
                  organization_name={selectedOrganization?.name}
                  isParentLoading={isLoading}
                  onOrganizationChange={(isChanging) => {
                    setIsLoading(isChanging)
                  }}
                />
              </TabPanel>
              <TabPanel
                header={
                  <GetTabHeader
                    header={"Form Customization"}
                    tooltip={
                      "Customize your RecoveryLink Kiosk forms connected to supported Templates."
                    }
                  />
                }
                disabled={disableTabView}
              >
                <KioskAdminFormList
                  kiosk_organization_id={selectedOrganization?.id}
                  organization_id={selectedOrganization?.organizationId}
                  isLoadingTemplates={isLoadingKioskTemplates}
                  templates={kioskTemplates}
                />
              </TabPanel>
              <TabPanel
                header={
                  <GetTabHeader
                    header={"First Time Visitor"}
                    tooltip={
                      "Configure the First Time Visitor options in your RecoveryLink Kiosk."
                    }
                  />
                }
                disabled={disableTabView}
              >
                <FirstTimeVisitorOptions
                  kiosk_organization_id={selectedOrganization?.id}
                  organization_id={selectedOrganization?.organizationId}
                />
              </TabPanel>
              <TabPanel
                header={
                  <GetTabHeader
                    header={"Activity Check-in"}
                    tooltip={
                      "Configure the Activity Check-In options in your RecoveryLink Kiosk."
                    }
                  />
                }
                disabled={disableTabView}
              >
                <ActivityCheckInOptions
                  kiosk_organization_id={selectedOrganization?.id}
                />
              </TabPanel>
              <TabPanel
                header={
                  <GetTabHeader
                    header={"Kiosk Assessments"}
                    tooltip={
                      "Configure the Assessment(s) available in your RecoveryLink Kiosk. Users will be invited to complete these after taking various actions at your RecoveryLink kiosk."
                    }
                  />
                }
                disabled={disableTabView}
              >
                <KioskAdditionalAssessmentsList
                  kioskOrganizationId={selectedOrganization?.id}
                  organizationId={selectedOrganization?.organizationId}
                  kioskTemplates={kioskTemplates}
                />
              </TabPanel>
              <TabPanel
                header={
                  <GetTabHeader
                    header={"Participant Management"}
                    tooltip={
                      "Configure options for individual participants in your RecoveryLink Kiosk. You may provide a personal note for an individual user at their next check-in or block access."
                    }
                  />
                }
                disabled={disableTabView}
              >
                <ParticipantManagement
                  kiosk_organization_id={selectedOrganization?.id}
                  organization_id={selectedOrganization?.organizationId}
                />
              </TabPanel>
            </TabView>
          </div>
        </div>
      </div>
    </>
  )
}
